/**
 * Admin dashboard: tryout creation + Tryout Form Templates (custom evaluation form builder).
 * See ./TryoutFormCreationNotes.md for handoff notes and file map.
 */
import './AdminDashboardPage.css';
import '../components/TryoutsList/TryoutsList.css';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { getFirestore } from 'firebase/firestore';

import AddablePill from '../components/AddablePill/AddablePill';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import CloseButton from '../components/common/CloseButton';
import ConfirmDialog from '../components/common/ConfirmDialog/ConfirmDialog';
import Container from '../components/common/Container';
import HamburgerIcon from '../components/common/HamburgerIcon';
import InfoCallout from '../components/common/InfoCallout/InfoCallout';
import Input from '../components/common/Input';
import LabeledField from '../components/common/LabeledField/LabeledField';
import FormEditor from '../components/FormEditor/FormEditor';
import FormsList from '../components/FormEditor/FormsList';
import SchemaPreview, {
  FORM_PREVIEW_MEDIA_QUERY,
} from '../components/FormEditor/SchemaPreview';
import GymLogos from '../components/GymLogos/GymLogos';
import PlaygroundSkillJudgeDemo from '../components/playgrounds/Playground';
import TryoutsList from '../components/TryoutsList/TryoutsList';
import { useAuth } from '../contexts/AuthContext';
import { usePostHog } from '@posthog/react';
import firebase from '../firebase';
import {
  deleteFormTemplate,
  fetchFormTemplates,
  saveFormTemplate,
} from '../services/formTemplateService';
import { fetchGymLogos } from '../services/gymLogoService';
import {
  createTryout,
  DEFAULT_ATHLETE_POSITIONS,
  DEFAULT_ATHLETE_STATUSES,
  updateTryoutForm,
} from '../services/tryoutService';
import {
  FormSchema,
  FormTemplate,
} from '../types/formSchema';
import { GymLogo } from '../types/gymLogo';
import { copyTextToClipboard } from '../utils/clipboard';

type DebugPreview = 'none' | 'error' | 'tryoutCode';
type DashboardView =
  | 'dashboard'
  | 'forms'
  | 'tryoutFormEdit'
  | 'gymLogos'
  | 'playgrounds';
type FormsSubview = { mode: 'list' } | { mode: 'edit'; template?: FormTemplate };

interface TryoutFormEditContext {
  tryoutId: string;
  tryoutName: string;
  formName: string;
  schema: FormSchema;
}

const DEBUG_TRYOUT_CODE = 'ABC123';
const DEBUG_TRYOUT_PASSWORD = 'WXYZ5678';

const DEFAULT_CREATE_TRYOUT_TEAMS = ['Yes', 'No', 'Callback'] as const;

type CreateModalPillField = 'teams' | 'athletePositions' | 'athleteStatuses';

/** Production: treat like dev build for dashboard debug UI (playgrounds, etc.). */
const DASHBOARD_DEBUG_EMAIL = 'paydrone@gmail.com';

const logoSrc = `${process.env.PUBLIC_URL}/logo.svg`;

const AdminDashboardPage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const posthog = usePostHog();
  const isDevBuild =
    process.env.NODE_ENV === 'development' ||
    currentUser?.email === DASHBOARD_DEBUG_EMAIL;
  const [tryoutCode, setTryoutCode] = useState<string | null>(null);
  const [tryoutPassword, setTryoutPassword] = useState<string | null>(null);
  const [tryoutName, setTryoutName] = useState('');
  const [tryoutPasswordInput, setTryoutPasswordInput] = useState('');
  const [createdTryoutName, setCreatedTryoutName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [debugPreview, setDebugPreview] = useState<DebugPreview>('none');
  const [tryoutsListRefreshKey, setTryoutsListRefreshKey] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const [activeView, setActiveView] = useState<DashboardView>('dashboard');

  useEffect(() => {
    if (!isDevBuild && activeView === 'playgrounds') {
      setActiveView('dashboard');
    }
  }, [isDevBuild, activeView]);
  const [formsSubview, setFormsSubview] = useState<FormsSubview>({ mode: 'list' });
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [templatesRefreshKey, setTemplatesRefreshKey] = useState(0);
  const [formsJsonOpen, setFormsJsonOpen] = useState(false);
  const [formsDebugDraft, setFormsDebugDraft] = useState<FormTemplate | null>(
    null,
  );
  const [tryoutFormDraft, setTryoutFormDraft] = useState<FormTemplate | null>(
    null,
  );
  const [formsJsonCopied, setFormsJsonCopied] = useState(false);
  const [previewWideEnough, setPreviewWideEnough] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia(FORM_PREVIEW_MEDIA_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(FORM_PREVIEW_MEDIA_QUERY);
    const update = (): void => setPreviewWideEnough(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [tryoutFormEditCtx, setTryoutFormEditCtx] =
    useState<TryoutFormEditContext | null>(null);
  const [, setTryoutFormSaving] = useState(false);
  const [tryoutFormError, setTryoutFormError] = useState('');
  const [createTryoutModalOpen, setCreateTryoutModalOpen] = useState(false);
  const [createModalTeams, setCreateModalTeams] = useState<string[]>([]);
  const [createModalAthletePositions, setCreateModalAthletePositions] = useState<
    string[]
  >([]);
  const [createModalAthleteStatuses, setCreateModalAthleteStatuses] = useState<
    string[]
  >([]);
  /** `undefined` = omit on create; `null` = no logo; `string` = logo doc id */
  const [createModalGymLogoId, setCreateModalGymLogoId] = useState<
    string | null | undefined
  >(undefined);
  const [createModalGymLogos, setCreateModalGymLogos] = useState<GymLogo[]>([]);
  const [createModalGymLogosLoading, setCreateModalGymLogosLoading] =
    useState(false);
  const [createModalLogoMenu, setCreateModalLogoMenu] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const [createModalPillDelete, setCreateModalPillDelete] = useState<{
    field: CreateModalPillField;
    index: number;
  } | null>(null);
  const createModalLogoAnchorRef = useRef<HTMLDivElement>(null);

  const db = getFirestore(firebase);

  const resetCreateTryoutModalFields = useCallback((): void => {
    setTryoutName('');
    setTryoutPasswordInput('');
    setSelectedTemplateId('');
    setError('');
    setTryoutCode(null);
    setTryoutPassword(null);
    setCreatedTryoutName(null);
    setCopySuccess(false);
    setCreateModalTeams([...DEFAULT_CREATE_TRYOUT_TEAMS]);
    setCreateModalAthletePositions([...DEFAULT_ATHLETE_POSITIONS]);
    setCreateModalAthleteStatuses([...DEFAULT_ATHLETE_STATUSES]);
    setCreateModalGymLogoId(undefined);
    setCreateModalLogoMenu(null);
    setCreateModalPillDelete(null);
  }, []);

  const handleCloseCreateTryoutModal = useCallback((): void => {
    if (loading) return;
    setCreateTryoutModalOpen(false);
    resetCreateTryoutModalFields();
  }, [loading, resetCreateTryoutModalFields]);

  const handleOpenCreateTryoutModal = useCallback((): void => {
    resetCreateTryoutModalFields();
    setCreateTryoutModalOpen(true);
  }, [resetCreateTryoutModalFields]);

  const tryoutsListHeaderRight = useMemo(
    () => (
      <Button type="button" onClick={handleOpenCreateTryoutModal}>
        New Tryout
      </Button>
    ),
    [handleOpenCreateTryoutModal],
  );

  useEffect(() => {
    if (!createTryoutModalOpen || !currentUser?.uid) {
      return;
    }
    let cancelled = false;
    setCreateModalGymLogosLoading(true);
    fetchGymLogos(db, currentUser.uid)
      .then((logos) => {
        if (!cancelled) setCreateModalGymLogos(logos);
      })
      .catch((err: unknown) => {
        console.error('Failed to load gym logos:', err);
      })
      .finally(() => {
        if (!cancelled) setCreateModalGymLogosLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [createTryoutModalOpen, currentUser?.uid, db]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Element;
      if (
        createModalLogoMenu
        && !target.closest('.admin-create-tryout-logo-anchor')
        && !target.closest('.admin-create-tryout-logo-dropdown')
      ) {
        setCreateModalLogoMenu(null);
      }
    };

    if (createTryoutModalOpen && createModalLogoMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [createTryoutModalOpen, createModalLogoMenu]);

  useEffect(() => {
    if (!createModalLogoMenu) return;
    const syncPosition = (): void => {
      const anchor = createModalLogoAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      setCreateModalLogoMenu((prev) =>
        prev
          ? {
              ...prev,
              top: rect.bottom + 4,
              right: window.innerWidth - rect.right,
            }
          : null,
      );
    };

    window.addEventListener('scroll', syncPosition, true);
    window.addEventListener('resize', syncPosition);
    return () => {
      window.removeEventListener('scroll', syncPosition, true);
      window.removeEventListener('resize', syncPosition);
    };
  }, [createModalLogoMenu]);

  useEffect(() => {
    if (!createTryoutModalOpen) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || loading) return;
      if (createModalPillDelete !== null) {
        setCreateModalPillDelete(null);
        return;
      }
      if (createModalLogoMenu) {
        setCreateModalLogoMenu(null);
        return;
      }
      handleCloseCreateTryoutModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    createTryoutModalOpen,
    loading,
    createModalLogoMenu,
    createModalPillDelete,
    handleCloseCreateTryoutModal,
  ]);

  const templatesLoadSignal = useMemo(
    () => ({
      uid: currentUser?.uid ?? null,
      refreshKey: templatesRefreshKey,
    }),
    [currentUser?.uid, templatesRefreshKey],
  );

  useEffect(() => {
    const { uid } = templatesLoadSignal;
    if (!uid) {
      return;
    }
    let cancelled = false;
    setTemplatesLoading(true);
    setTemplatesError('');
    fetchFormTemplates(db, uid)
      .then((items) => {
        if (!cancelled) {
          setFormTemplates(items);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTemplatesError('Failed to load forms');
          console.error(err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTemplatesLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [db, templatesLoadSignal]);

  const effectiveTryoutCode =
    tryoutCode ??
    (debugPreview === 'tryoutCode' ? DEBUG_TRYOUT_CODE : null);

  const effectiveTryoutPassword =
    tryoutPassword ??
    (debugPreview === 'tryoutCode' ? DEBUG_TRYOUT_PASSWORD : null);

  const displayError =
    debugPreview === 'error'
      ? 'Failed to create tryout'
      : debugPreview === 'tryoutCode'
        ? ''
        : error;

  const trimmedTryoutName = tryoutName.trim();
  const trimmedTryoutPassword = tryoutPasswordInput.trim();
  const canCreateTryout = trimmedTryoutName.length > 0 && trimmedTryoutPassword.length > 0;

  async function handleCreateTryout(): Promise<void> {
    if (!currentUser) {
      return;
    }
    if (!trimmedTryoutName) {
      setError('Enter a tryout name');
      return;
    }
    try {
      setLoading(true);
      setError('');

      const selectedTemplate = formTemplates.find((t) => t.id === selectedTemplateId);
      const schema: FormSchema = selectedTemplate
        ? structuredClone(selectedTemplate.schema)
        : { groups: [] };
      const formName = selectedTemplate?.name ?? '';

      const { code, tryoutId, password } = await createTryout(
        trimmedTryoutName,
        trimmedTryoutPassword,
        schema,
        formName,
        {
          teams: createModalTeams,
          athletePositions: createModalAthletePositions,
          athleteStatuses: createModalAthleteStatuses,
          ...(createModalGymLogoId !== undefined
            ? {
                gymLogoId: createModalGymLogoId,
                gymLogoUrl:
                  createModalGymLogoId === null
                    ? null
                    : createModalGymLogos.find((l) => l.id === createModalGymLogoId)?.imgUrl ?? null,
              }
            : {}),
        },
      );

      setCreatedTryoutName(trimmedTryoutName);
      setTryoutCode(code);
      setTryoutPassword(password);
      setTryoutName('');
      setSelectedTemplateId('');
      setCreateModalTeams([...DEFAULT_CREATE_TRYOUT_TEAMS]);
      setCreateModalAthletePositions([...DEFAULT_ATHLETE_POSITIONS]);
      setCreateModalAthleteStatuses([...DEFAULT_ATHLETE_STATUSES]);
      setCreateModalGymLogoId(undefined);
        setCreateModalLogoMenu(null);
      setTryoutsListRefreshKey((k) => k + 1);
      posthog?.capture('tryout_created', { tryout_id: tryoutId, tryout_name: trimmedTryoutName });
    } catch (createErr: unknown) {
      setError('Failed to create tryout');
      posthog?.captureException(createErr);
      console.error(createErr);
    } finally {
      setLoading(false);
    }
  }

  const toggleCreateModalLogoMenu = (): void => {
    setCreateModalLogoMenu((prev) => {
      if (prev) return null;
      const anchor = createModalLogoAnchorRef.current;
      if (!anchor) return null;
      const rect = anchor.getBoundingClientRect();
      return {
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      };
    });
  };

  const handleSelectCreateModalGymLogo = (logoId: string | null): void => {
    setCreateModalGymLogoId(logoId);
    setCreateModalLogoMenu(null);
  };

  const handleAddCreateModalTeam = async (name: string): Promise<void> => {
    setCreateModalTeams((prev) => [...prev, name]);
  };

  const handleAddCreateModalAthletePosition = async (name: string): Promise<void> => {
    setCreateModalAthletePositions((prev) => [...prev, name]);
  };

  const handleAddCreateModalAthleteStatus = async (name: string): Promise<void> => {
    setCreateModalAthleteStatuses((prev) => [...prev, name]);
  };

  const pendingCreateModalPillLabel =
    createModalPillDelete != null
      ? (createModalPillDelete.field === 'teams'
          ? createModalTeams[createModalPillDelete.index]
          : createModalPillDelete.field === 'athletePositions'
            ? createModalAthletePositions[createModalPillDelete.index]
            : createModalAthleteStatuses[createModalPillDelete.index]) ?? ''
      : '';

  const createModalPillDeleteTitle =
    createModalPillDelete?.field === 'teams'
      ? 'Remove team?'
      : createModalPillDelete?.field === 'athletePositions'
        ? 'Remove athlete position?'
        : createModalPillDelete != null
          ? 'Remove athlete status?'
          : 'Remove?';

  const createModalPillDeleteMessage =
    pendingCreateModalPillLabel
      ? `Are you sure you want to delete “${pendingCreateModalPillLabel}”?`
      : createModalPillDelete?.field === 'teams'
        ? 'Are you sure you want to delete this team?'
        : createModalPillDelete?.field === 'athletePositions'
          ? 'Are you sure you want to delete this athlete position?'
          : 'Are you sure you want to delete this athlete status?';

  const confirmRemoveCreateModalPill = (): void => {
    if (createModalPillDelete === null) {
      return;
    }
    const { field, index } = createModalPillDelete;
    setCreateModalPillDelete(null);
    if (field === 'teams') {
      setCreateModalTeams((prev) => prev.filter((_, i) => i !== index));
    } else if (field === 'athletePositions') {
      setCreateModalAthletePositions((prev) => prev.filter((_, i) => i !== index));
    } else {
      setCreateModalAthleteStatuses((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleCopyLink = (): void => {
    if (!effectiveTryoutCode) {
      return;
    }
    const joinUrl = `${window.location.origin}/join?code=${effectiveTryoutCode}`;
    void copyTextToClipboard(joinUrl).then((didCopy) => {
      if (!didCopy) {
        return;
      }
      setCopySuccess(true);
      window.setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const handleOpenLink = (): void => {
    if (!effectiveTryoutCode) {
      return;
    }
    const joinUrl = `${window.location.origin}/join?code=${effectiveTryoutCode}`;
    window.open(joinUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDebugError = (): void => {
    setDebugPreview((p) => (p === 'error' ? 'none' : 'error'));
  };

  const handleDebugTryoutCode = (): void => {
    setDebugPreview((p) => (p === 'tryoutCode' ? 'none' : 'tryoutCode'));
  };

  const handleMenuOpen = (): void => setMenuOpen(true);

  const handleMenuClose = useCallback((): void => {
    menuTriggerRef.current?.focus({ preventScroll: true });
    setMenuOpen(false);
  }, []);

  const handleNavTo = (view: DashboardView): void => {
    setActiveView(view);
    if (view === 'forms') {
      setFormsSubview({ mode: 'list' });
    }
    if (view === 'dashboard') {
      setFormsJsonOpen(false);
      setFormsDebugDraft(null);
      setTryoutFormDraft(null);
      setTryoutFormEditCtx(null);
    }
    handleMenuClose();
  };

  const handleSaveTemplate = async (template: FormTemplate): Promise<void> => {
    if (!currentUser) {
      setTemplatesError('You must be signed in to save a form.');
      return;
    }
    setTemplatesError('');
    try {
      await saveFormTemplate(db, currentUser.uid, template);
      posthog?.capture('form_template_saved', { template_id: template.id, template_name: template.name });
      setTemplatesRefreshKey((k) => k + 1);
      setFormsDebugDraft(null);
      setFormsSubview({ mode: 'list' });
    } catch (err: unknown) {
      setTemplatesError('Failed to save form');
      posthog?.captureException(err);
      console.error(err);
    }
  };

  const handleDeleteTemplate = async (templateId: string): Promise<void> => {
    if (!currentUser) {
      setTemplatesError('You must be signed in to delete a form.');
      return;
    }
    const previous = formTemplates;
    setFormTemplates((prev) => prev.filter((t) => t.id !== templateId));
    setTemplatesError('');
    try {
      await deleteFormTemplate(db, currentUser.uid, templateId);
      posthog?.capture('form_template_deleted', { template_id: templateId });
      setTemplatesRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      setFormTemplates(previous);
      setTemplatesError('Failed to delete form');
      posthog?.captureException(err);
      console.error(err);
    }
  };

  const handleFormDebugSnapshot = useCallback((template: FormTemplate) => {
    setFormsDebugDraft(template);
  }, []);

  const handleTryoutFormSnapshot = useCallback((template: FormTemplate) => {
    setTryoutFormDraft(template);
  }, []);

  const handleEditTryoutForm = useCallback(
    (tryoutId: string, tryoutName: string, formName: string, schema: FormSchema | null): void => {
      setTryoutFormEditCtx({
        tryoutId,
        tryoutName,
        formName,
        schema: schema ?? { groups: [] },
      });
      setTryoutFormError('');
      setActiveView('tryoutFormEdit');
    },
    [],
  );

  const handleSaveTryoutForm = async (template: FormTemplate): Promise<void> => {
    if (!tryoutFormEditCtx) {
      return;
    }
    setTryoutFormSaving(true);
    setTryoutFormError('');
    try {
      await updateTryoutForm(db, tryoutFormEditCtx.tryoutId, template.name, template.schema);
      posthog?.capture('tryout_form_saved', { tryout_id: tryoutFormEditCtx.tryoutId, form_name: template.name });
      setTryoutsListRefreshKey((k) => k + 1);
      setTryoutFormDraft(null);
      setTryoutFormEditCtx(null);
      setActiveView('dashboard');
    } catch (err: unknown) {
      setTryoutFormError('Failed to save evaluation form');
      posthog?.captureException(err);
      console.error(err);
    } finally {
      setTryoutFormSaving(false);
    }
  };

  const formsDebugJsonString =
    formsSubview.mode === 'edit'
      ? formsDebugDraft != null
        ? JSON.stringify(formsDebugDraft, null, 2)
        : ''
      : JSON.stringify(formTemplates, null, 2);

  // Non-null only when the live preview pane should render: viewport is wide
  // enough AND the draft has at least one section to show.
  const previewVisibleDraft: FormTemplate | null =
    previewWideEnough
    && formsDebugDraft
    && formsDebugDraft.schema.groups.length > 0
      ? formsDebugDraft
      : null;

  const tryoutFormPreviewDraft: FormTemplate | null =
    previewWideEnough
    && tryoutFormDraft
    && tryoutFormDraft.schema.groups.length > 0
      ? tryoutFormDraft
      : null;

  const handleCopyFormsJson = (): void => {
    const text =
      formsDebugJsonString.length > 0
        ? formsDebugJsonString
        : JSON.stringify(formTemplates, null, 2);
    void copyTextToClipboard(text).then(
      (didCopy) => {
        if (!didCopy) {
          return;
        }
        setFormsJsonCopied(true);
        window.setTimeout(() => setFormsJsonCopied(false), 2000);
      },
    );
  };

  const createModalSelectedGymLogo =
    createModalGymLogoId != null
      ? createModalGymLogos.find((l) => l.id === createModalGymLogoId)
      : undefined;

  const dashboardDebugControls =
    isDevBuild && activeView === 'dashboard' ? (
      <div className="admin-dashboard-debug">
        <Button
          type="button"
          variant="secondary"
          compact
          onClick={handleDebugTryoutCode}
        >
          {debugPreview === 'tryoutCode' ? 'Hide' : 'Show'} tryout code card
          (debug)
        </Button>
        <Button
          type="button"
          variant="secondary"
          compact
          onClick={handleDebugError}
        >
          {debugPreview === 'error' ? 'Hide' : 'Show'} error banner (debug)
        </Button>
      </div>
    ) : null;

  const formsDebugControls =
    isDevBuild && activeView === 'forms' ? (
      <div className="admin-dashboard-forms-debug">
        <Button
          type="button"
          variant="secondary"
          compact
          onClick={() => setFormsJsonOpen((o) => !o)}
        >
          {formsJsonOpen ? 'Hide' : 'Show'} forms JSON (debug)
        </Button>
        {formsJsonOpen ? (
          <div className="admin-dashboard-forms-debug-panel">
            <div className="admin-dashboard-forms-debug-actions">
              <Button
                type="button"
                variant="secondary"
                compact
                onClick={handleCopyFormsJson}
                disabled={
                  formsSubview.mode === 'edit' && formsDebugDraft == null
                }
              >
                {formsJsonCopied ? 'Copied!' : 'Copy JSON'}
              </Button>
            </div>
            <pre
              className="admin-dashboard-forms-debug-pre"
              tabIndex={0}
            >
              {formsSubview.mode === 'edit' && formsDebugDraft == null ? (
                <span className="admin-dashboard-forms-debug-placeholder">
                  Loading draft…
                </span>
              ) : (
                formsDebugJsonString
              )}
            </pre>
          </div>
        ) : null}
      </div>
    ) : null;

  return (
    <div className="admin-dashboard-shell">
      <div
        className={`admin-dashboard-overlay ${menuOpen ? 'open' : ''}`}
        onClick={handleMenuClose}
        aria-hidden="true"
      />

      <header className="admin-dashboard-header">
        <div className="admin-dashboard-logo-wrap">
          <img
            src={logoSrc}
            alt="CheerEval"
            className="admin-dashboard-header-logo"
          />
        </div>
        <HamburgerIcon ref={menuTriggerRef} onClick={handleMenuOpen} />
      </header>

      <hr className="admin-dashboard-divider" />

      <Container className="admin-dashboard-container">
        {activeView === 'tryoutFormEdit' ? (
          <InfoCallout className="admin-dashboard-form-edit-callout">
            Editing a form on a tryout will not cause changes to your saved
            Tryout Form Template.
          </InfoCallout>
        ) : null}
        <Card
          className={[
            'admin-dashboard-main-card',
            (activeView === 'tryoutFormEdit' || (activeView === 'forms' && formsSubview.mode === 'edit'))
              && 'admin-dashboard-main-card--form-edit',
            ((activeView === 'forms' && formsSubview.mode === 'edit') || activeView === 'tryoutFormEdit')
              && previewWideEnough
              && 'admin-dashboard-main-card--form-edit-wide',
          ].filter(Boolean).join(' ')}
        >
          {activeView === 'dashboard' ? (
            <>
              <TryoutsList
                refreshKey={tryoutsListRefreshKey}
                onEditForm={handleEditTryoutForm}
                headerRight={tryoutsListHeaderRight}
              />
            </>
          ) : null}

          {activeView === 'gymLogos' ? <GymLogos /> : null}

          {activeView === 'playgrounds' && isDevBuild ? (
            <div className="admin-dashboard-playgrounds">
              <h2 className="admin-dashboard-subheading">Playgrounds</h2>
              <p className="admin-dashboard-body">
                Development-only space for UI experiments. This tab is hidden in
                production builds.
              </p>
              <PlaygroundSkillJudgeDemo />
            </div>
          ) : null}

          {activeView === 'tryoutFormEdit' && tryoutFormEditCtx ? (
            <>
              {tryoutFormError ? (
                <div className="admin-dashboard-error">{tryoutFormError}</div>
              ) : null}
              <div
                className={
                  tryoutFormPreviewDraft ? 'fe-with-preview' : undefined
                }
              >
                <div className="fe-with-preview-editor">
                  <FormEditor
                    initial={{
                      id: tryoutFormEditCtx.tryoutId,
                      name: tryoutFormEditCtx.formName,
                      schema: tryoutFormEditCtx.schema,
                    }}
                    onSave={(tpl) => void handleSaveTryoutForm(tpl)}
                    onCancel={() => {
                      setTryoutFormDraft(null);
                      setTryoutFormEditCtx(null);
                      setActiveView('dashboard');
                    }}
                    onDebugSnapshot={handleTryoutFormSnapshot}
                  />
                </div>
                {tryoutFormPreviewDraft ? (
                  <aside className="fe-with-preview-pane">
                    <SchemaPreview draft={tryoutFormPreviewDraft} />
                  </aside>
                ) : null}
              </div>
            </>
          ) : null}

          {activeView === 'forms' ? (
            <>
              {templatesError ? (
                <div className="admin-dashboard-error">{templatesError}</div>
              ) : null}
              {formsSubview.mode === 'edit' ? (
                <div
                  className={
                    previewVisibleDraft ? 'fe-with-preview' : undefined
                  }
                >
                  <div className="fe-with-preview-editor">
                    <FormEditor
                      initial={formsSubview.template}
                      onSave={(tpl) => void handleSaveTemplate(tpl)}
                      onCancel={() => {
                        setFormsDebugDraft(null);
                        setFormsSubview({ mode: 'list' });
                      }}
                      onDebugSnapshot={handleFormDebugSnapshot}
                    />
                  </div>
                  {previewVisibleDraft ? (
                    <aside className="fe-with-preview-pane">
                      <SchemaPreview draft={previewVisibleDraft} />
                    </aside>
                  ) : null}
                </div>
              ) : templatesLoading && formTemplates.length === 0 ? (
                <p className="admin-dashboard-body">Loading forms…</p>
              ) : (
                <FormsList
                  templates={formTemplates}
                  onCreateNew={() => {
                    setFormsDebugDraft(null);
                    setFormsSubview({ mode: 'edit' });
                  }}
                  onEdit={(tpl) => {
                    setFormsDebugDraft(null);
                    setFormsSubview({ mode: 'edit', template: tpl });
                  }}
                  onDelete={(tplId) => void handleDeleteTemplate(tplId)}
                />
              )}
            </>
          ) : null}
        </Card>
        {dashboardDebugControls}
        {formsDebugControls}
      </Container>

      <aside
        className={`admin-dashboard-side-menu ${menuOpen ? 'open' : ''}`}
        inert={menuOpen ? undefined : true}
      >
        <CloseButton onClick={handleMenuClose} />
        {currentUser?.email ? (
          <p className="admin-dashboard-menu-email">{currentUser.email}</p>
        ) : null}
        <nav className="admin-dashboard-menu-nav">
          <button
            type="button"
            className={`admin-dashboard-menu-nav-item ${activeView === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleNavTo('dashboard')}
          >
            My Tryouts
          </button>
          <button
            type="button"
            className={`admin-dashboard-menu-nav-item ${activeView === 'forms' ? 'active' : ''}`}
            onClick={() => handleNavTo('forms')}
          >
            Tryout Form Templates
          </button>
          <button
            type="button"
            className={`admin-dashboard-menu-nav-item ${activeView === 'gymLogos' ? 'active' : ''}`}
            onClick={() => handleNavTo('gymLogos')}
          >
            Gym Logos
          </button>
          {isDevBuild ? (
            <button
              type="button"
              className={`admin-dashboard-menu-nav-item ${activeView === 'playgrounds' ? 'active' : ''}`}
              onClick={() => handleNavTo('playgrounds')}
            >
              Playgrounds
            </button>
          ) : null}
        </nav>
        <div className="admin-dashboard-menu-actions">
          <Button
            type="button"
            variant="primary"
            fullWidth
            onClick={() => {
              handleMenuClose();
              void logout();
            }}
          >
            Log out
          </Button>
        </div>
      </aside>

      {createTryoutModalOpen ? (
        <>
          {createPortal(
            <div
              className="admin-create-tryout-overlay"
              role="presentation"
              onClick={() => {
                if (!loading) handleCloseCreateTryoutModal();
              }}
            >
              <div
                className="admin-create-tryout-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-create-tryout-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="admin-create-tryout-header">
                  <h2
                    id="admin-create-tryout-title"
                    className="admin-create-tryout-title"
                  >
                    New Tryout
                  </h2>
                  <CloseButton
                    className="admin-create-tryout-close"
                    onClick={() => {
                      if (!loading) handleCloseCreateTryoutModal();
                    }}
                  />
                </div>

                <div className="admin-create-tryout-body tryout-generator">
                  <div className="admin-create-tryout-body-padded">
                    {displayError ? (
                      <div className="admin-dashboard-error">{displayError}</div>
                    ) : null}

                    {!tryoutCode ? (
                      <>
                        <LabeledField label="Tryout name" htmlFor="create-tryout-name-input">
                          <Input
                            id="create-tryout-name-input"
                            value={tryoutName}
                            onChange={(e) => {
                              setTryoutName(e.target.value.slice(0, 120));
                            }}
                            placeholder="e.g. Spring 2026 — U18"
                            autoComplete="off"
                            disabled={loading}
                          />
                        </LabeledField>

                        <LabeledField label="Join password" htmlFor="create-tryout-password-input">
                          <Input
                            id="create-tryout-password-input"
                            value={tryoutPasswordInput}
                            onChange={(e) => {
                              setTryoutPasswordInput(e.target.value.slice(0, 50));
                            }}
                            placeholder="e.g. CHEER2026"
                            autoComplete="off"
                            disabled={loading}
                          />
                        </LabeledField>

                        <LabeledField
                          label="Evaluation form"
                          htmlFor="tryout-form-template-select"
                        >
                          <select
                            id="tryout-form-template-select"
                            className="tryout-form-select"
                            value={selectedTemplateId}
                            onChange={(e) => setSelectedTemplateId(e.target.value)}
                            disabled={loading}
                          >
                            <option value="">Blank (no form)</option>
                            {formTemplates.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        </LabeledField>
                      </>
                    ) : null}
                  </div>

                  {!tryoutCode ? (
                    <div className="tryouts-list-teams-section admin-create-tryout-pills-shell">
                      <div className="admin-create-tryout-teams-logo-row">
                        <div className="admin-create-tryout-teams-cell">
                          <AddablePill
                            sectionLabel="Teams"
                            items={createModalTeams}
                            disabled={loading}
                            onAdd={handleAddCreateModalTeam}
                            onRemoveItem={(index) =>
                              setCreateModalPillDelete({ field: 'teams', index })
                            }
                            inputPlaceholder="Team name"
                            addButtonAriaLabel="Add team"
                            commitButtonAriaLabel="Add team"
                            removeButtonAriaLabel={(label) => `Remove team ${label}`}
                          />
                        </div>
                        <div className="admin-create-tryout-logo-cell">
                          <div
                            className="tryouts-list-logo-selector-wrapper admin-create-tryout-logo-anchor"
                            ref={createModalLogoAnchorRef}
                          >
                            <button
                              id="create-tryout-gym-logo-trigger"
                              type="button"
                              className="tryouts-list-logo-button"
                              onClick={toggleCreateModalLogoMenu}
                              disabled={loading || createModalGymLogosLoading}
                              aria-label="Select gym logo"
                              aria-haspopup="listbox"
                              aria-expanded={createModalLogoMenu !== null}
                            >
                              {createModalSelectedGymLogo ? (
                                <img
                                  src={createModalSelectedGymLogo.imgUrl}
                                  alt="Gym logo"
                                  className="tryouts-list-logo-image"
                                />
                              ) : (
                                <span className="tryouts-list-logo-placeholder">
                                  GYM LOGO
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="tryouts-list-pill-groups admin-create-tryout-pills-shell-sub">
                        <AddablePill
                          sectionLabel="Athlete Positions"
                          items={createModalAthletePositions}
                          inputPlaceholder="Position name"
                          addButtonAriaLabel="Add athlete position"
                          commitButtonAriaLabel="Add athlete position"
                          removeButtonAriaLabel={(label) =>
                            `Remove athlete position ${label}`
                          }
                          disabled={loading}
                          onAdd={handleAddCreateModalAthletePosition}
                          onRemoveItem={(index) =>
                            setCreateModalPillDelete({
                              field: 'athletePositions',
                              index,
                            })
                          }
                        />
                        <AddablePill
                          sectionLabel="Athlete Status"
                          items={createModalAthleteStatuses}
                          inputPlaceholder="Status name"
                          addButtonAriaLabel="Add athlete status"
                          commitButtonAriaLabel="Add athlete status"
                          removeButtonAriaLabel={(label) =>
                            `Remove athlete status ${label}`
                          }
                          disabled={loading}
                          onAdd={handleAddCreateModalAthleteStatus}
                          onRemoveItem={(index) =>
                            setCreateModalPillDelete({
                              field: 'athleteStatuses',
                              index,
                            })
                          }
                        />
                      </div>
                    </div>
                  ) : null}

                  {effectiveTryoutCode ? (
                    <div className="admin-create-tryout-body-padded">
                      <div className="tryout-code-center">
                        <Card variant="success" className="tryout-code">
                          {tryoutCode && createdTryoutName ? (
                            <p className="tryout-code-created-name">
                              {createdTryoutName}
                            </p>
                          ) : null}
                          <h3 className="admin-dashboard-subheading tryout-code-heading">
                            Tryout Code:
                          </h3>
                          <div className="code-display">{effectiveTryoutCode}</div>
                          <h3 className="admin-dashboard-subheading tryout-code-heading">
                            Tryout Password:
                          </h3>
                          <div className="code-display">{effectiveTryoutPassword}</div>
                          <p className="admin-dashboard-hint">
                            Share both the code and password with coaches to join at
                            cheereval.ca/join
                          </p>
                          <div className="share-buttons">
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={handleCopyLink}
                            >
                              {copySuccess ? 'Copied!' : 'Copy Link'}
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={handleOpenLink}
                            >
                              Open Tryout
                            </Button>
                          </div>
                        </Card>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="admin-create-tryout-footer">
                  {effectiveTryoutCode ? (
                    <Button
                      type="button"
                      fullWidth
                      onClick={handleCloseCreateTryoutModal}
                    >
                      Done
                    </Button>
                  ) : (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        fullWidth
                        onClick={handleCloseCreateTryoutModal}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        fullWidth
                        onClick={() => void handleCreateTryout()}
                        disabled={loading || !canCreateTryout}
                      >
                        {loading ? 'Creating...' : 'Create Tryout'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )}
          {createModalLogoMenu
            ? createPortal(
                <div
                  className="tryouts-list-logo-dropdown admin-create-tryout-logo-dropdown"
                  style={{
                    top: createModalLogoMenu.top,
                    right: createModalLogoMenu.right,
                  }}
                >
                  <button
                    type="button"
                    className="tryouts-list-logo-dropdown-item"
                    onClick={() => handleSelectCreateModalGymLogo(null)}
                  >
                    <span className="tryouts-list-logo-dropdown-empty">No logo</span>
                  </button>
                  {createModalGymLogos.map((logo) => (
                    <button
                      key={logo.id}
                      type="button"
                      className="tryouts-list-logo-dropdown-item"
                      onClick={() => handleSelectCreateModalGymLogo(logo.id)}
                    >
                      <img
                        src={logo.imgUrl}
                        alt={logo.name}
                        className="tryouts-list-logo-dropdown-thumb"
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="tryouts-list-logo-dropdown-name">{logo.name}</span>
                    </button>
                  ))}
                </div>,
                document.body,
              )
            : null}
        </>
      ) : null}

      <ConfirmDialog
        open={createTryoutModalOpen && createModalPillDelete !== null}
        title={createModalPillDeleteTitle}
        message={createModalPillDeleteMessage}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmRemoveCreateModalPill}
        onCancel={() => setCreateModalPillDelete(null)}
      />
    </div>
  );
};

export default AdminDashboardPage;
